import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { RootState } from 'constants/interfaces'
import { AiAvatar } from 'components/CommonComponents/AIAvatar'
import { AiCameraIcon, QuestionIcon } from 'components/CommonComponents/JSXIcons'
import { RadioCheckbox } from 'components/TasksV2/UI/RadioCheckbox'
import { Button } from 'components/ui'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { createPulseButtonCss } from 'components/TasksV2/UI/pulseButton'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { AIStepType, useAILoadingContext } from '../../AIFormStepContext'
import { useAiCreationText } from '../../hooks/useAiCreationText'
import { captureEvent } from 'services/posthog'
import styles from './styles.module.css'

export default function AIOnboardingStep() {
  const { selectedSubtaskType, setSelectedSubtaskType } = useAILoadingContext()
  const { setStepStack } = useFormStepContext()
  const color = useSelector((state: RootState) => state.general.theme?.elementsColor || '#FF2D78')
  const t = useAiCreationText()
  const rtl = useSelector(isRtlSelector)
  const pulse = useMemo(
    () => [0, 1100].map((delay) => (!selectedSubtaskType ? createPulseButtonCss(color, delay) : undefined)),
    [color, selectedSubtaskType]
  )

  return (
    <section className={styles.onboarding}>
      <div className={styles.content}>
        <div className={styles.avatar}>
          <AiAvatar listening size={180} />
        </div>
        <h1>{t.onboardingTitle}</h1>
        <p>{t.onboardingHint}</p>
        <ol className={styles.steps}>
          {[t.step1, t.step2, t.step3].map((label, index) => (
            <li key={index} data-active={index === 0 || !!selectedSubtaskType}>
              <span className={styles.number}>{index + 1}</span>
              <div>
                <strong>{label}</strong>
                {index === 0 && (
                  <div className={styles.choices}>
                    {(['image', 'question'] as const).map((type, choiceIndex) => (
                      <div
                        key={type}
                        css={pulse[choiceIndex]}
                        className={styles.choice}
                        data-selected={selectedSubtaskType === type}
                      >
                        <RadioCheckbox
                          inputType="radio"
                          name="ai-audit-type"
                          value={type === 'image' ? t.photo : t.question}
                          isChecked={selectedSubtaskType === type}
                          onChange={() => setSelectedSubtaskType(type)}
                          fillColor={color}
                        >
                          <div className={styles.choiceContent}>
                            {type === 'image' ? (
                              <AiCameraIcon width="48px" height="48px" />
                            ) : (
                              <QuestionIcon width="48px" height="48px" />
                            )}
                            <strong>{type === 'image' ? t.photo : t.question}</strong>
                            <span>{type === 'image' ? t.photoHint : t.questionHint}</span>
                          </div>
                        </RadioCheckbox>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
      <footer>
        <Button
          type="button"
          isDisabled={!selectedSubtaskType}
          endIcon={rtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
          onClick={() => {
            captureEvent('ai_task_creation_started')
            setStepStack((stack) => [...stack, AIStepType.AI_PROMPT])
          }}
        >
          {t.start}
        </Button>
      </footer>
    </section>
  )
}
